import React, { useEffect, useState, useCallback, useMemo } from 'react';
import SecretariatShell from './SecretariatShell';
import { SectionTitle } from './PageShell';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { ChevronLeft, ChevronRight, X, Trash2, Loader2, RefreshCw } from 'lucide-react';

const HOURS = Array.from({ length: 12 }, (_, i) => 8 + i); // 8h → 19h
const DAYS_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

function iso(d) { return d.toISOString().slice(0, 10); }
function mondayOf(d) { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); return x; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }

const TYPE_LABEL = { conduite: 'Conduite', code: 'Code', examen: 'Examen', rvp: 'RVP', moto_plateau: '🏍 Moto · Plateau' };
const MOTO_COLOR = '#8b5cf6';
const lessonColor = (l) => (l.type === 'moto_plateau' ? MOTO_COLOR : l.teacher_color);

export default function SecretariatPlanningPage() {
  const { user, loading: authLoading } = useAuth();
  const [mode, setMode] = useState(() => (typeof window !== 'undefined' && window.innerWidth < 768 ? 'jour' : 'semaine'));
  const [anchor, setAnchor] = useState(() => new Date());
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [teacherId, setTeacherId] = useState('');
  const [lessons, setLessons] = useState([]);
  const [modal, setModal] = useState(null); // {date, hour} pour créer, {lesson} pour éditer
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  const weekStart = useMemo(() => mondayOf(anchor), [anchor]);
  const monthStart = useMemo(() => new Date(anchor.getFullYear(), anchor.getMonth(), 1), [anchor]);

  const range = useMemo(() => {
    if (mode === 'jour') return [iso(anchor), iso(anchor)];
    if (mode === 'semaine') return [iso(weekStart), iso(addDays(weekStart, 6))];
    const first = mondayOf(monthStart);
    return [iso(first), iso(addDays(first, 41))];
  }, [mode, anchor, weekStart, monthStart]);

  const refresh = useCallback(() => {
    api.secLessons(range[0], range[1], mode === 'semaine' ? teacherId || undefined : undefined)
      .then((d) => setLessons(d.items || [])).catch(() => {});
  }, [range, teacherId, mode]);

  useEffect(() => {
    if (authLoading || !user) return;
    api.secTeachers().then((d) => {
      setTeachers(d.items || []);
      setTeacherId((cur) => cur || d.items?.[0]?.id || '');
    }).catch(() => {});
    api.secEleves().then((d) => setStudents(d.items || [])).catch(() => {});
  }, [authLoading, user]);

  useEffect(() => { if (!authLoading && user) refresh(); }, [authLoading, user, refresh]);

  // Auto-actualisation : toutes les 30 s + au retour sur l'onglet
  useEffect(() => {
    if (authLoading || !user) return;
    const iv = setInterval(refresh, 30000);
    window.addEventListener('focus', refresh);
    return () => { clearInterval(iv); window.removeEventListener('focus', refresh); };
  }, [authLoading, user, refresh]);

  const monthCells = useMemo(() => {
    const first = mondayOf(monthStart);
    return Array.from({ length: 42 }, (_, i) => addDays(first, i));
  }, [monthStart]);

  if (authLoading) return <SecretariatShell><div className="p-12 text-white/50">Chargement…</div></SecretariatShell>;
  if (!user || (user.role !== 'secretaire' && !user.is_admin && !user.secretariat_access)) return <SecretariatShell><div className="p-12 text-center text-white/60">Accès réservé au secrétariat.</div></SecretariatShell>;

  const openCreate = (date, hour) => {
    setErrMsg('');
    setForm({ student_file_id: students[0]?.id || '', teacher_file_id: teacherId || teachers[0]?.id || '', date, start_time: `${String(hour).padStart(2, '0')}:00`, duration_min: 60, type: 'conduite', vehicle_type: 'bm', notes: '' });
    setModal({ kind: 'create' });
  };
  const openEdit = (lesson) => {
    setErrMsg('');
    setForm({ ...lesson });
    setModal({ kind: 'edit' });
  };

  const save = async () => {
    setSaving(true); setErrMsg('');
    try {
      if (modal.kind === 'create') {
        await api.secCreateLesson({ student_file_id: form.student_file_id, teacher_file_id: form.teacher_file_id, date: form.date, start_time: form.start_time, duration_min: Number(form.duration_min), type: form.type, vehicle_type: form.vehicle_type || 'bm', notes: form.notes || '' });
      } else {
        await api.secUpdateLesson(form.id, { date: form.date, start_time: form.start_time, duration_min: Number(form.duration_min), type: form.type, vehicle_type: form.vehicle_type || 'bm', notes: form.notes || '', status: form.status, teacher_file_id: form.teacher_file_id, student_file_id: form.student_file_id });
      }
      setModal(null); refresh();
    } catch (e) { setErrMsg(e?.response?.data?.detail || "Erreur d'enregistrement"); }
    setSaving(false);
  };

  const removeLesson = async () => {
    if (!window.confirm('Supprimer cette leçon ?')) return;
    await api.secDeleteLesson(form.id).catch(() => {});
    setModal(null); refresh();
  };

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const lessonsAt = (date, hour) => lessons.filter((l) => l.date === date && parseInt(l.start_time.slice(0, 2), 10) === hour);

  const navLabel = mode === 'jour'
    ? anchor.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })
    : mode === 'semaine'
      ? `Semaine du ${weekStart.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}`
      : anchor.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

  const shift = (n) => setAnchor((a) => (mode === 'jour' ? addDays(a, n) : mode === 'semaine' ? addDays(a, n * 7) : new Date(a.getFullYear(), a.getMonth() + n, 1)));

  const STATUS_CLS = { planifie: '', effectue: 'opacity-60 line-through', annule: 'opacity-30 line-through', absent_excuse: 'opacity-45', absent_non_excuse: 'opacity-45' };

  return (
    <SecretariatShell>
      <section className="max-w-[1400px] mx-auto px-4 lg:px-6 mt-8" data-testid="secretariat-planning">
        <SectionTitle kicker="Secrétariat · Planning" title="Planning des leçons" sub="Cliquez sur un créneau libre pour affecter une leçon, sur une leçon pour la modifier." />

        <div className="flex flex-wrap items-center gap-2 mb-5">
          <div className="flex rounded-full border border-white/15 overflow-hidden">
            {['jour', 'semaine', 'mois'].map((m) => (
              <button key={m} onClick={() => setMode(m)} data-testid={`planning-mode-${m}`} className={`px-4 h-9 text-[12.5px] font-semibold ${mode === m ? 'bg-amber-500 text-black' : 'text-white/60'}`}>{m === 'jour' ? 'Jour' : m === 'semaine' ? 'Semaine' : 'Mois'}</button>
            ))}
          </div>
          <div className="flex items-center gap-1 ml-2">
            <button onClick={() => shift(-1)} data-testid="planning-prev" className="w-9 h-9 rounded-full border border-white/15 text-white/70 hover:text-white inline-flex items-center justify-center"><ChevronLeft size={15} /></button>
            <button onClick={() => setAnchor(new Date())} className="px-3 h-9 rounded-full border border-white/15 text-white/70 hover:text-white text-[12px]">Aujourd'hui</button>
            <button onClick={() => shift(1)} data-testid="planning-next" className="w-9 h-9 rounded-full border border-white/15 text-white/70 hover:text-white inline-flex items-center justify-center"><ChevronRight size={15} /></button>
            <button onClick={refresh} data-testid="planning-refresh-btn" title="Actualiser (auto toutes les 30 s)" className="w-9 h-9 rounded-full border border-white/15 text-white/70 hover:text-white inline-flex items-center justify-center"><RefreshCw size={13} /></button>
          </div>
          <span className="text-white/70 text-[14px] font-semibold capitalize ml-1">{navLabel}</span>
          {mode === 'semaine' && (
            <div className="flex items-center gap-1.5 ml-auto flex-wrap">
              {teachers.map((t) => (
                <button key={t.id} onClick={() => setTeacherId(t.id)} data-testid={`planning-teacher-${t.id}`} className={`px-3.5 h-8 rounded-full text-[12px] font-semibold border inline-flex items-center gap-1.5 ${teacherId === t.id ? 'bg-white text-black border-white' : 'text-white/60 border-white/15'}`}>
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: t.color }} /> {t.name}
                </button>
              ))}
              {teachers.length === 0 && <span className="text-white/40 text-[12.5px]">Créez d'abord un enseignant →</span>}
            </div>
          )}
        </div>

        {mode === 'jour' ? (
          <div data-testid="planning-day-view">
            {(() => {
              const dayIso = iso(anchor);
              const isToday = dayIso === iso(new Date());
              const dayLessons = [...lessons].filter((l) => l.date === dayIso).sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
              const STATUS_BADGE_DAY = {
                effectue: ['Effectuée ✓', 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300'],
                annule: ['Annulée', 'bg-red-500/15 border-red-500/40 text-red-300'],
                absent_excuse: ['Abs. excusée', 'bg-orange-500/15 border-orange-500/40 text-orange-300'],
                absent_non_excuse: ['Abs. non excusée', 'bg-red-500/15 border-red-500/40 text-red-300'],
              };
              return (
                <>
                  <div className={`rounded-xl border px-4 py-3 mb-3 flex items-center justify-between gap-2 ${isToday ? 'border-amber-500/40 bg-amber-500/[0.07]' : 'border-white/10 bg-white/[0.02]'}`}>
                    <div className="text-white text-[13.5px] font-semibold">
                      {isToday ? '📍 ' : ''}{dayLessons.length} leçon{dayLessons.length > 1 ? 's' : ''}
                      {dayLessons.length > 0 && <span className="text-white/55 font-normal"> · de {dayLessons[0].start_time} à {dayLessons[dayLessons.length - 1].start_time}</span>}
                    </div>
                    <button onClick={() => openCreate(dayIso, 9)} data-testid="day-add-lesson" className="h-9 px-3.5 rounded-full bg-amber-500 hover:bg-amber-400 text-black text-[12px] font-bold shrink-0">+ Leçon</button>
                  </div>
                  {dayLessons.length === 0 ? (
                    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-8 text-center text-white/45 text-[13px]" data-testid="day-empty">Aucune leçon ce jour.</div>
                  ) : (
                    <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] divide-y divide-white/5">
                      {dayLessons.map((l) => {
                        const badge = STATUS_BADGE_DAY[l.status];
                        return (
                          <button key={l.id} onClick={() => openEdit(l)} data-testid={`day-lesson-${l.id}`} className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-white/[0.03] transition">
                            <span className="w-1.5 h-10 rounded-full shrink-0" style={{ background: lessonColor(l) }} />
                            <span className="font-mono-spec text-white text-[14px] font-bold w-14 shrink-0">{l.start_time}</span>
                            <span className="flex-1 min-w-0">
                              <span className="block text-white text-[13.5px] font-semibold truncate">{l.student_name}</span>
                              <span className="block text-white/45 text-[12px] truncate">{l.teacher_name} · {TYPE_LABEL[l.type]} · {l.duration_min} min{l.notes ? ` · ${l.notes}` : ''}</span>
                            </span>
                            {badge && <span className={`shrink-0 px-2 py-0.5 rounded-md border text-[10.5px] font-bold ${badge[0] && badge[1]}`}>{badge[0]}</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        ) : mode === 'semaine' ? (
          <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] overflow-x-auto">
            <table className="w-full min-w-[820px] border-collapse">
              <thead>
                <tr>
                  <th className="w-14 p-2 text-white/30 text-[10px] font-mono-spec">h</th>
                  {weekDays.map((d, i) => (
                    <th key={i} className={`p-2 text-[12px] font-semibold border-l border-white/5 ${iso(d) === iso(new Date()) ? 'text-amber-400' : 'text-white/60'}`}>{DAYS_FR[i]} {d.getDate()}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {HOURS.map((h) => (
                  <tr key={h} className="border-t border-white/5">
                    <td className="p-1.5 text-white/35 text-[11px] font-mono-spec text-center align-top">{h}h</td>
                    {weekDays.map((d, i) => {
                      const date = iso(d);
                      const cell = lessonsAt(date, h);
                      return (
                        <td key={i} onClick={() => cell.length === 0 && teacherId && openCreate(date, h)} data-testid={`slot-${date}-${h}`} className="relative border-l border-white/5 align-top p-1 h-14 cursor-pointer hover:bg-white/[0.03] transition">
                          {cell.map((l, li) => {
                            // La hauteur du bloc suit la durée (56px par heure) → une leçon
                            // de 120 min recouvre 2 cases. Si 2 leçons sur le même créneau
                            // (plateau moto), partage horizontal de la colonne.
                            const startMin = parseInt(l.start_time.slice(3, 5), 10) || 0;
                            const hPx = Math.max(44, ((l.duration_min || 60) / 60) * 56 - 8);
                            const w = cell.length > 1 ? `${100 / cell.length}%` : undefined;
                            return (
                              <button key={l.id} onClick={(e) => { e.stopPropagation(); openEdit(l); }} data-testid={`lesson-${l.id}`}
                                className={`absolute z-10 text-left rounded-lg px-2 py-1.5 text-[11px] leading-tight text-white overflow-hidden ${STATUS_CLS[l.status] || ''}`}
                                style={{
                                  background: `${lessonColor(l)}cc`,
                                  top: `${4 + (startMin / 60) * 56}px`,
                                  height: `${hPx}px`,
                                  left: cell.length > 1 ? `calc(${(100 / cell.length) * li}% + 4px)` : '4px',
                                  width: cell.length > 1 ? `calc(${100 / cell.length}% - 8px)` : 'calc(100% - 8px)',
                                }}>
                                <div className="font-bold truncate">{l.start_time} · {l.student_name}</div>
                                <div className="opacity-80 truncate">{TYPE_LABEL[l.type]} · {l.duration_min} min</div>
                              </button>
                            );
                          })}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] overflow-hidden">
            <div className="grid grid-cols-7 border-b border-white/8">
              {DAYS_FR.map((d) => <div key={d} className="p-2 text-center text-white/50 text-[11px] font-semibold">{d}</div>)}
            </div>
            <div className="grid grid-cols-7">
              {monthCells.map((d, i) => {
                const date = iso(d);
                const inMonth = d.getMonth() === anchor.getMonth();
                const dayLessons = lessons.filter((l) => l.date === date && l.status !== 'annule');
                return (
                  <div key={i} onClick={() => { setAnchor(d); setMode('jour'); }} className={`min-h-[84px] border-b border-r border-white/5 p-1.5 cursor-pointer hover:bg-white/[0.03] ${inMonth ? '' : 'opacity-30'}`} data-testid={`month-day-${date}`}>
                    <div className={`text-[11px] font-mono-spec mb-1 ${date === iso(new Date()) ? 'text-amber-400 font-bold' : 'text-white/45'}`}>{d.getDate()}</div>
                    {dayLessons.slice(0, 3).map((l) => (
                      <div key={l.id} className="flex items-center gap-1 text-[10px] text-white/70 truncate">
                        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: lessonColor(l) }} />{l.start_time} {l.student_name.split(' ')[0]}
                      </div>
                    ))}
                    {dayLessons.length > 3 && <div className="text-[10px] text-amber-400">+{dayLessons.length - 3}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Modal création/édition */}
        {modal && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setModal(null)}>
            <div className="w-full max-w-md rounded-2xl border border-white/15 bg-[#111113] p-6" onClick={(e) => e.stopPropagation()} data-testid="lesson-modal">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-serif-editorial text-white text-[20px]">{modal.kind === 'create' ? 'Nouvelle leçon' : 'Modifier la leçon'}</h3>
                <button onClick={() => setModal(null)} className="text-white/50 hover:text-white"><X size={18} /></button>
              </div>
              <div className="space-y-3">
                <label className="block text-[12px] text-white/55">Élève
                  <select value={form.student_file_id} onChange={(e) => setForm({ ...form, student_file_id: e.target.value })} data-testid="lesson-student-select" className="mt-1 w-full h-10 rounded-lg bg-black/40 border border-white/15 px-3 text-white text-[13px]">
                    {students.map((s) => <option key={s.id} value={s.id}>{s.name} — {s.formula}</option>)}
                  </select>
                </label>
                <label className="block text-[12px] text-white/55">Enseignant
                  <select value={form.teacher_file_id} onChange={(e) => setForm({ ...form, teacher_file_id: e.target.value })} data-testid="lesson-teacher-select" className="mt-1 w-full h-10 rounded-lg bg-black/40 border border-white/15 px-3 text-white text-[13px]">
                    {teachers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <label className="block text-[12px] text-white/55">Date
                    <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 w-full h-10 rounded-lg bg-black/40 border border-white/15 px-2 text-white text-[12.5px]" />
                  </label>
                  <label className="block text-[12px] text-white/55">Heure
                    <input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="lesson-time-input" className="mt-1 w-full h-10 rounded-lg bg-black/40 border border-white/15 px-2 text-white text-[12.5px]" />
                  </label>
                  <label className="block text-[12px] text-white/55">Durée
                    <select value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} className="mt-1 w-full h-10 rounded-lg bg-black/40 border border-white/15 px-2 text-white text-[12.5px]">
                      {[60, 90, 120].map((d) => <option key={d} value={d}>{d} min</option>)}
                    </select>
                  </label>
                </div>
                <div className="flex gap-2">
                  {Object.entries(TYPE_LABEL).map(([v, l]) => (
                    <button key={v} onClick={() => setForm({ ...form, type: v })} className={`px-3 h-8 rounded-full border text-[11.5px] font-semibold ${form.type === v ? 'bg-amber-500 text-black border-amber-500' : 'text-white/60 border-white/15'}`}>{l}</button>
                  ))}
                </div>
                <div>
                  <div className="text-[11px] text-white/45 mb-1.5">Moyen utilisé (DSR)</div>
                  <div className="flex gap-2">
                    {[['bm', 'Boîte manuelle'], ['ba', 'Boîte auto'], ['simulateur', 'Simulateur']].map(([v, l]) => (
                      <button key={v} onClick={() => setForm({ ...form, vehicle_type: v })} data-testid={`lesson-vehicle-${v}`} className={`px-3 h-8 rounded-full border text-[11.5px] font-semibold ${(form.vehicle_type || 'bm') === v ? 'bg-sky-500 text-black border-sky-500' : 'text-white/60 border-white/15'}`}>{l}</button>
                    ))}
                  </div>
                </div>
                {modal.kind === 'edit' && (
                  <div>
                    <div className="text-[11px] text-white/45 mb-1.5">Statut de présence (DSR)</div>
                    <div className="flex flex-wrap gap-2">
                      {[['planifie', 'Planifiée'], ['effectue', 'Effectuée ✓'], ['annule', 'Annulée'], ['absent_excuse', 'Abs. excusée'], ['absent_non_excuse', 'Abs. non excusée']].map(([v, l]) => (
                        <button key={v} onClick={() => setForm({ ...form, status: v })} data-testid={`lesson-status-${v}`} className={`px-3 h-8 rounded-full border text-[11.5px] font-semibold ${form.status === v ? (v === 'effectue' ? 'bg-emerald-500 text-black border-emerald-500' : v === 'annule' ? 'bg-red-500 text-white border-red-500' : v.startsWith('absent') ? 'bg-orange-500 text-black border-orange-500' : 'bg-white text-black border-white') : 'text-white/60 border-white/15'}`}>{l}</button>
                      ))}
                    </div>
                  </div>
                )}
                <input value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes (optionnel)" className="w-full h-10 rounded-lg bg-black/40 border border-white/15 px-3 text-white text-[13px]" />
                {errMsg && <div className="text-red-300 text-[12.5px]" data-testid="lesson-error">⚠️ {errMsg}</div>}
                <div className="flex items-center gap-2 pt-1">
                  <button onClick={save} disabled={saving || !form.student_file_id || !form.teacher_file_id} data-testid="lesson-save-btn" className="flex-1 h-10 rounded-full bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-black text-[13px] font-bold inline-flex items-center justify-center gap-2">
                    {saving && <Loader2 size={14} className="animate-spin" />} Enregistrer
                  </button>
                  {modal.kind === 'edit' && <button onClick={removeLesson} data-testid="lesson-delete-btn" className="w-10 h-10 rounded-full border border-red-500/40 text-red-300 inline-flex items-center justify-center"><Trash2 size={14} /></button>}
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
    </SecretariatShell>
  );
}
